/**
 * Strategy step main view.
 *
 * Layout: SVG sizing sheet (left) | Device selection (right)
 * Replaces the viewport area entirely when the strategy step is active.
 */

import { Loader2 } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import { CatalogSelector } from './catalog-selector';
import { PartSelectorList } from './part-selector-list';
import svgContent from '../../assets/AAA_Sizing_Sheet.svg?raw';
import { getMeasurementFromPlaceholder } from '../../domain/evar-svg-mapping';
import { useCatalog } from '../../hooks/use-catalog';
import { useStrategyState } from '../../hooks/use-strategy-state';
import { buildMeasurementLookup } from '../../strategy/evar-strategy-binding';

import type { FieldSelections, Side } from '@/workflows/vascular/common/catalog';
import { SvgSizingSheet } from '@/workflows/vascular/common/components/svg-sizing-sheet';
import { useSizingState } from '@/workflows/vascular/common/hooks/use-sizing-state';

export function EvarStrategyMain() {
    const strategy = useStrategyState();
    const sizing = useSizingState();
    const { catalog, loading, error } = useCatalog(strategy?.selectedCatalogId ?? null);

    const measurementLookup = useMemo(() => buildMeasurementLookup(sizing), [sizing]);

    const handleAdd = useCallback(
        (partId: string, side: Side, fieldSelections: FieldSelections) => {
            if (!strategy || !catalog) return;
            strategy.addPartFromCatalog(catalog, partId, side, fieldSelections);
        },
        [strategy, catalog]
    );

    if (!strategy) {
        return (
            <div className="flex h-full items-center justify-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                Initializing strategy...
            </div>
        );
    }

    return (
        <div className="flex h-full overflow-hidden bg-background">
            {/* ── Left column: catalog selection + part selectors ── */}
            <div className="flex-1 flex flex-col overflow-hidden">
                <CatalogSelector selectedId={strategy.selectedCatalogId} onSelect={strategy.selectCatalog} />

                {/* Catalog content area */}
                <div className="flex-1 overflow-y-auto">
                    {loading && (
                        <div className="flex items-center justify-center py-8 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            Loading catalog...
                        </div>
                    )}

                    {error && <div className="px-3 py-4 text-xs text-destructive">{error}</div>}

                    {!loading && !error && !catalog && strategy.selectedCatalogId && (
                        <div className="px-3 py-4 text-xs text-muted-foreground">
                            Catalog data not yet available. The catalog JSON file needs to be converted from
                            the SQL database.
                        </div>
                    )}

                    {!strategy.selectedCatalogId && (
                        <div className="px-3 py-8 text-center text-xs text-muted-foreground">
                            Select a catalog above to begin device selection.
                        </div>
                    )}

                    {catalog && (
                        <PartSelectorList
                            parts={catalog.parts}
                            measurementLookup={measurementLookup}
                            onAdd={handleAdd}
                            assembly={strategy.assembly}
                            catalogId={strategy.selectedCatalogId!}
                            introductionSide={strategy.introductionSide}
                        />
                    )}
                </div>
            </div>

            {/* ── Right column: SVG sizing sheet ── */}
            <div className="xl:w-2/3 w-1/2 border-r border-border overflow-hidden flex flex-col">
                <div className="flex-1 min-h-0 overflow-hidden p-2">
                    <SvgSizingSheet
                        svgContent={svgContent}
                        getMeasurementFromPlaceholder={getMeasurementFromPlaceholder}
                        readOnly
                    />
                </div>
            </div>
        </div>
    );
}
